"use client";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

interface ServiceCardProps {
    service: {
        title: string;
        description: string;
        images: string[];
        features: string[];
    };
    index: number;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service, index }) => {
    let serviceId = service.title
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/&/g, "and")
        .replace(/'/g, "")
        .replace(/,/g, "")
        .replace("--", "-");

    if (service.title.toLowerCase().includes("post production")) {
        serviceId = "post-production";
    }

    // Carousel states
    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(1);

    useEffect(() => {
        const interval = setInterval(() => {
            setDirection(1);
            setCurrentIndex((prev) => (prev + 1) % service.images.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [service.images.length]);

    // Framer Motion variants for sliding
    const variants = {
        enter: (dir: number) => ({
            x: dir > 0 ? "100%" : "-100%",
            opacity: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
        },
        exit: (dir: number) => ({
            x: dir > 0 ? "-100%" : "100%",
            opacity: 0,
        }),
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="bg-gray-900/50 rounded-xl overflow-hidden border border-gray-800 hover:border-burnt-orange/50 transition-colors group"
        >
            {/* Image Slider */}
            <div className="relative h-60 w-full overflow-hidden">
                <AnimatePresence custom={direction} initial={false}>
                    <motion.img
                        key={currentIndex}
                        src={service.images[currentIndex]}
                        alt={`${service.title} ${currentIndex + 1}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                            x: { type: "spring", stiffness: 300, damping: 30 },
                            opacity: { duration: 0.3 },
                        }}
                        onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://via.placeholder.com/600x400/2b2b2b/d2691e?text=${service.title}+${currentIndex + 1}`;
                        }}
                    />
                </AnimatePresence>
            </div>

            {/* Card Content */}
            <div className="p-8">
                <h3 className="text-2xl font-bold text-white mb-4">
                    {service.title}
                </h3>
                <p className="text-gray-400 mb-6">{service.description}</p>

                <h4 className="text-white font-medium mb-3">What's included:</h4>
                <ul className="space-y-2 mb-6">
                    {service.features.map((feature, i) => (
                        <li key={i} className="flex items-start">
                            <svg
                                className="h-5 w-5 text-burnt-orange mr-2 mt-0.5 flex-shrink-0"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                            <span className="text-gray-400">{feature}</span>
                        </li>
                    ))}
                </ul>

                <Link
                    to={`/services/${serviceId}`}
                    className="text-burnt-orange font-medium flex items-center group-hover:underline"
                >
                    Learn more
                    <svg
                        className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </Link>
            </div>
        </motion.div>
    );
};

export default ServiceCard;
